import {
	CanvasTexture,
	DataTexture,
	DoubleSide,
	Group,
	MathUtils,
	Mesh,
	MeshPhongMaterial,
	PlaneGeometry,
	Texture,
	Vector2,
	VideoTexture
} from 'three'
import { loadImage, loadVideo } from './getTexture'
import App from './App'
import blank from '../assets/blank.png'

type Options = {
	app: App
	displacementTexture: DataTexture
	normalTexture: Texture
	parent: Group
	config: any
}

const Y_RANGE = 1.8

export default class Sticker {
	private readonly app: App
	private readonly parent: Group
	private readonly group: Group = new Group()
	private readonly mesh: Mesh<PlaneGeometry, MeshPhongMaterial>
	private readonly material: MeshPhongMaterial
	private readonly speed: number
	private readonly scale: number
	private readonly rotation: number
	private readonly turns: number
	private readonly turn_offset: number
	private readonly distance: number
	private readonly offset: number
	private readonly image: string
	private readonly video: string

	private isLoaded = false
	private aspect = 1
	private vfc?: number

	public type?: 'image' | 'svg' | 'video'
	public hasVideoFrameCallback = false

	constructor(options: Options) {
		const { app, displacementTexture, normalTexture, config, parent } = options
		this.app = app
		this.parent = parent
		this.image = config.image
		this.video = config.video

		this.speed = config.speed
		this.scale = config.scale
		this.rotation = config.rotation
		this.turns = config.turns
		this.turn_offset = config.turn_offset
		this.distance = config.distance
		this.offset = config.offset

		this.material = new MeshPhongMaterial({
			transparent: true,
			alphaTest: 0.5,
			side: DoubleSide,
			map: new Texture(blank),
			displacementMap: displacementTexture,
			normalMap: normalTexture
		})

		this.material.onBeforeCompile = (shader: any) => {
			shader.uniforms.translate = { value: new Vector2(0, -3) }
			shader.uniforms.scale = { value: new Vector2(this.scale, this.scale * 6) }
			shader.uniforms.aspect = { value: new Vector2(this.aspect * 2, 2) }
			shader.uniforms.rotation = { value: MathUtils.degToRad(this.rotation) }
			shader.uniforms.distance = { value: this.distance }

			//language=glsl
			shader.vertexShader = shader.vertexShader.replace(
				'#include <clipping_planes_pars_vertex>',
				`
					#include <clipping_planes_pars_vertex>
					uniform float rotation;
					uniform float distance;
					uniform vec2 aspect;
					uniform vec2 translate;
					uniform vec2 scale;

					varying vec2 vDisplacementUv;

					#define BASE_DISTANCE 1.4
					#define DISPLACEMENT_DISTANCE 1.25

					float map(float value, float min1, float max1, float min2, float max2) {
						return min2 + (value - min1) * (max2 - min2) / (max1 - min1);
					}

					vec2 rotate(vec2 v, float a) {
						float s = sin(a);
						float c = cos(a);
						mat2 m = mat2(c, -s, s, c);
						return m * v;
					}
				`
			)

			//language=glsl
			shader.vertexShader = shader.vertexShader.replace(
				'#include <project_vertex>',
				`
					vec2 offset = (position.xy / aspect);
					vec2 rotated = rotate(offset, rotation);
					vec2 pos = rotated * scale + translate;
					float u = fract(pos.x + 0.5);
					float v = map(pos.y, -${Y_RANGE}, ${Y_RANGE}, 0.0, 1.0);
					vec2 displacementUv = vec2(u, v);
					float displacement = (texture2D(displacementMap, displacementUv).r - 0.5) * 2.0;

					float baseDist = BASE_DISTANCE + DISPLACEMENT_DISTANCE * displacement;
					vec2 positionOnCircle = rotate(vec2(0.0, baseDist + distance), pos.x * PI2);
					vec3 displacedPosition = vec3(positionOnCircle.x, pos.y, positionOnCircle.y);
					vec4 mvPosition = vec4(displacedPosition, 1.0);

					vDisplacementUv = displacementUv;
					mvPosition = modelViewMatrix * mvPosition;
					gl_Position = projectionMatrix * viewMatrix * vec4(displacedPosition, 1.0);
				`
			)

			//language=glsl
			shader.fragmentShader = shader.fragmentShader.replace(
				'#include <common>',
				`
					#include <common>
					varying vec2 vDisplacementUv;
				`
			)

			//language=glsl
			shader.fragmentShader = shader.fragmentShader.replace(
				'#include <normal_fragment_maps>',
				`
					#include <normal_fragment_maps>
					normal = texture2D(normalMap, vDisplacementUv).xyz * 2.0 - 1.0;
				`
			)

			this.material.userData.shader = shader
		}

		const geometry = new PlaneGeometry(2, 2, 100, 100)
		this.mesh = new Mesh(geometry, this.material)

		this.group.add(this.mesh)
		this.parent.add(this.group)
	}

	async load() {
		if (this.image) {
			const extension = this.image.split('.').pop()

			const image = await loadImage(this.image)
			const { naturalWidth, naturalHeight } = image
			this.aspect = naturalHeight / naturalWidth

			if (extension === 'svg') {
				this.type = 'svg'

				const canvas = document.createElement('canvas')
				canvas.width = naturalWidth
				canvas.height = naturalHeight
				const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

				ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

				this.material.map = new CanvasTexture(canvas)
			} else {
				this.type = 'image'
				this.material.map = new Texture(image)
			}
		} else {
			this.type = 'video'
			const video = await loadVideo(this.video)
			const { videoWidth, videoHeight } = video
			this.aspect = videoHeight / videoWidth

			this.hasVideoFrameCallback = 'requestVideoFrameCallback' in video

			if (this.hasVideoFrameCallback) {
				const update = () => {
					// @ts-ignore
					this.vfc = video.requestVideoFrameCallback(update)
					this.app.requestRender()
				}
				// @ts-ignore
				this.vfc = video.requestVideoFrameCallback(update)
			}

			this.material.map = new VideoTexture(video)
		}

		this.material.map.needsUpdate = true
		this.isLoaded = true
	}

	dispose() {
		if (this.type === 'video') {
			const video = this.material.map?.image as HTMLVideoElement
			video.pause()
			if ('requestVideoFrameCallback' in video) {
				// @ts-ignore
				video.cancelVideoFrameCallback(this.vfc)
			}
		}

		this.mesh.geometry.dispose()
		this.material.map?.dispose()
		this.material.dispose()

		this.group.remove(this.mesh)
		this.parent.remove(this.group)
	}

	update(elapsed: number): void {
		if (!this.material.userData.shader || !this.isLoaded) return

		const e = elapsed * this.speed - this.offset

		this.group.visible = e >= 0 && e <= 1

		if (this.type === 'video') {
			const video = this.material.map?.image as HTMLVideoElement
			if (video.paused && this.group.visible) {
				video.play()
			} else if (!video.paused && !this.group.visible) {
				video.pause()
			}
		}

		if (!this.group.visible) return

		const x = MathUtils.mapLinear(
			e,
			0,
			1,
			this.turns * -0.5 + this.turn_offset,
			this.turns * 0.5 + this.turn_offset
		)
		const y = MathUtils.mapLinear(e, 0, 1, Y_RANGE * -1.5, Y_RANGE * 1.5)

		this.material.userData.shader.uniforms.translate.value.x = x
		this.material.userData.shader.uniforms.translate.value.y = y

		this.material.userData.shader.uniforms.aspect.value.x = this.aspect * 2
		this.material.userData.shader.uniforms.aspect.value.y = 2
	}
}
